import React, { useEffect } from 'react';
import { FormContextValues, useForm } from 'react-hook-form';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { useMediaQuery } from 'react-responsive';

import {
  PersonalInfoProps,
  PERSONAL_INFO_FORM_FIELD_LABELS,
  PERSONAL_INFO_FORM_FIELD_NAMES,
  PERSONAL_INFO_FORM_FIELD_PLACHOLDERS,
} from 'shared/constants/ekyc';
import Button from 'shared/modules/common/components/atoms/Button';
import CheckBox from 'shared/modules/common/components/atoms/CheckBox';
import TextInput from 'shared/modules/common/components/atoms/TextInput';
import DatePicker from 'shared/modules/common/components/molecules/DatePicker';
import { calculateAgeFromDate } from 'shared/utils/datetime';
import validator, { Rule } from 'shared/utils/validator';

type Props = {
  onNextButtonPress: () => void;
  form: FormContextValues<PersonalInfoProps>;
  isParentalConsentChecked: boolean;
  onParentalConsentCheckToggle: (checked: boolean) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onFieldBlur?: (
    field:
      | 'lastName'
      | 'firstName'
      | 'lastNameKana'
      | 'firstNameKana'
      | 'dateOfBirth'
  ) => void;
};

const PersonalInfo = (props: Props) => {
  const {
    onNextButtonPress,
    isParentalConsentChecked,
    onParentalConsentCheckToggle,
    form,
    onFocus,
    onBlur,
    onFieldBlur,
  } = props;
  const isBigScreen = useMediaQuery({ minWidth: 768 });

  useEffect(() => {
    onFocus?.();

    return () => onBlur?.();
  }, []);

  const shouldRequireParentalConsent = (dateString: string) => {
    if (!dateString) {
      return false;
    }

    return (
      calculateAgeFromDate(dateString) >= 18 &&
      calculateAgeFromDate(dateString) < 20
    );
  };

  useEffect(() => {
    const dateOfBirth = form.getValues().dateOfBirth;

    form.register('dateOfBirth', {
      validate: validator.getRules(
        isParentalConsentChecked
          ? ['mustBeValidDate']
          : ['mustBeValidDate', 'mustBeInValidDateOfBirthRange']
      ),
    });

    form.setValue('dateOfBirth', dateOfBirth);
  }, [isParentalConsentChecked]);

  useEffect(() => {
    if (!shouldRequireParentalConsent(form.getValues('dateOfBirth'))) {
      onParentalConsentCheckToggle(false);
    }
  }, [form.getValues().dateOfBirth]);

  return (
    <View>
      <View style={styles.panel}>
        <Text
          style={styles.notifTitle}
        >{`自動入力された情報をご確認ください`}</Text>
        <Text style={styles.notifBody}>
          {`身分証明書と完全に一致する情報を入力してください。情報が一致しない場合、本人確認ができません。\n\nミドルネームをお持ちの方は、姓・名のどちらかにスペースを入れずに入力してください。\n\n身分証明書のお名前が、アルファベット、漢字、通称名など複数の表記がある場合も、完全に一致する情報を入力してください。`}
        </Text>
      </View>
      <View style={styles.panel}>
        <View
          style={styles.form}
          {...Platform.select({ web: { accessibilityRole: 'form' } })}
        >
          <View>
            {PERSONAL_INFO_FORM_FIELD_NAMES.map((field, index) => {
              if (field === 'dateOfBirth') {
                return (
                  <DatePicker
                    key={index}
                    label={PERSONAL_INFO_FORM_FIELD_LABELS[index]}
                    placeholder="YYYY-MM-DD"
                    value={form.getValues(field) as string}
                    onSelect={(dateOfBirth) => {
                      form.setValue(field, dateOfBirth);
                    }}
                    onBlur={() => {
                      onFieldBlur?.(field);
                      form.triggerValidation(field);
                    }}
                    message={(form.errors[field]?.message as string) || ''}
                    state={form.errors[field]?.message ? 'error' : undefined}
                    selectButtonText="保存する"
                    showAsDialog={isBigScreen}
                    id="ekyc-name-form-birthday-picker"
                  />
                );
              } else {
                return (
                  <TextInput
                    key={index}
                    value={form.getValues(field) as string}
                    label={PERSONAL_INFO_FORM_FIELD_LABELS[index]}
                    placeholder={PERSONAL_INFO_FORM_FIELD_PLACHOLDERS[index]}
                    onChangeText={(text) => form.setValue(field, text)}
                    onBlur={() => {
                      onFieldBlur?.(field);
                      form.triggerValidation(field);
                    }}
                    message={(form.errors[field]?.message as string) || ''}
                    state={form.errors[field]?.message ? 'error' : undefined}
                    id={`ekyc-name-form-${field.toLowerCase()}-field`}
                    style={styles.marginBottom}
                  />
                );
              }
            })}
            {shouldRequireParentalConsent(form.getValues('dateOfBirth')) && (
              <View style={styles.checkboxContainer}>
                <CheckBox
                  checked={isParentalConsentChecked}
                  onChange={(checked) => onParentalConsentCheckToggle(checked)}
                  style={styles.checkbox}
                  id="ekyc-personal-info-form-parental-consent-checkbox"
                />
                <Text style={styles.checkboxLabel}>
                  {`保護者の同意を得た上で利用する`}
                </Text>
              </View>
            )}
          </View>
        </View>
      </View>
      <View
        style={[
          styles.buttonContainer,
          isBigScreen && styles.buttonContainerBigScreen,
        ]}
      >
        <Button
          title="次へ"
          onPress={async () => {
            const isValid = await form.triggerValidation();

            if (isValid) {
              onNextButtonPress();
            }
          }}
          id="ekyc-personal-info-form-next-button"
          disabled={
            !form.formState.dirty ||
            (shouldRequireParentalConsent(form.getValues('dateOfBirth')) &&
              !isParentalConsentChecked)
          }
        />
      </View>
    </View>
  );
};

export const usePersonalInfoFormWithValidations = (
  props: PersonalInfoProps
) => {
  const form = useForm({
    defaultValues: props,
  });

  const kanjiRules: Rule[] = [
    'mustNotBeEmptyOrSpace',
    'mustNotContainNonPrintableChars',
    'mustNotBeEmptyOrSpace',
  ];

  const kanaRules: Rule[] = [...kanjiRules, 'mustBeKatakanaOrSpaces'];

  useEffect(() => {
    PERSONAL_INFO_FORM_FIELD_NAMES.forEach((name) => {
      if (name === 'dateOfBirth') {
        form.register(
          { name },
          {
            validate: validator.getRules([
              'mustBeValidDate',
              'mustBeInValidDateOfBirthRange',
            ]),
          }
        );
      } else {
        form.register(
          {
            name,
          },
          {
            validate:
              name === PERSONAL_INFO_FORM_FIELD_NAMES[0] ||
              name === PERSONAL_INFO_FORM_FIELD_NAMES[1]
                ? validator.getRules(kanjiRules)
                : validator.getRules(kanaRules),
          }
        );
      }
    });
  }, [form.register]);

  form.watch();

  return form;
};

const styles = StyleSheet.create({
  panel: {
    padding: 24,
    borderRadius: 16,
    backgroundColor: '#fff',
    marginVertical: 12,
  },
  notifTitle: {
    fontSize: 13,
    color: '#FF2751',
    fontWeight: '600',
    marginBottom: 16,
  },
  notifBody: {
    fontSize: 13,
    color: '#1C1C1C',
  },
  form: {
    flex: 1,
    justifyContent: 'space-between',
  },
  marginBottom: {
    marginBottom: 24,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 24,
  },
  checkbox: {
    width: 24,
    height: 24,
    marginRight: 16,
  },
  checkboxLabel: {
    fontSize: 14,
    lineHeight: 22,
  },
  buttonContainer: {
    flex: 1,
    flexDirection: 'column',
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 12,
  },
  buttonContainerBigScreen: {
    paddingTop: 48,
  },
});

export default PersonalInfo;
